import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import Breadcrumbs from '../marketing/components/Breadcrumbs';
import { getCalculator } from '../marketing/data';
import { CALCULATOR_DEFS } from '../marketing/calculatorDefs';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const categoryLabel: Record<string, string> = { investment: 'Investment', compliance: 'Compliance', tax: 'Tax' };

const buildDefaults = (slug: string): Record<string, number> => {
  const def = CALCULATOR_DEFS[slug] || CALCULATOR_DEFS['rental-yield-calculator'];
  const inputs: Record<string, number> = {};
  def.fields.forEach((f) => {
    inputs[f.key] = f.value;
  });
  return inputs;
};

const CalculatorPage: React.FC = () => {
  useMarketingFonts();
  const { slug } = useParams<{ slug: string }>();
  const calc = getCalculator(slug);
  const defSlug = calc?.slug || 'rental-yield-calculator';
  const def = CALCULATOR_DEFS[defSlug] || CALCULATOR_DEFS['rental-yield-calculator'];

  const [inputs, setInputs] = useState<Record<string, number>>(() => buildDefaults(defSlug));

  useEffect(() => {
    setInputs(buildDefaults(defSlug));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defSlug]);

  useSeoHead({
    title: calc ? `${calc.name} | MillOS` : 'MillOS',
    description: calc?.description || '',
    canonicalPath: calc ? `/calculators/${calc.slug}` : undefined,
  });

  if (!calc) return <Navigate to="/calculators" replace />;

  const out = def.compute(inputs);

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <Breadcrumbs maxWidth={900} items={[{ label: 'Home', to: '/' }, { label: 'Calculators', to: '/calculators' }, { label: calc.name }]} />

      <Box component="section" sx={{ padding: { xs: '24px 16px 40px', md: '28px 52px 40px' } }}>
        <Box sx={{ maxWidth: 900, margin: '0 auto' }}>
          <Box sx={{ display: 'inline-block', background: C.tintBlue, color: C.accent, borderRadius: '32px', padding: '6px 14px', fontSize: 11, fontWeight: 500, letterSpacing: '0.06rem', textTransform: 'uppercase' }}>
            {categoryLabel[calc.category] || 'Tool'}
          </Box>
          <Box component="h1" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2.25rem,4.4vw,3.5rem)', lineHeight: 1.08, letterSpacing: '-0.03em', marginTop: '18px' }}>
            {calc.name}
          </Box>
          <Box sx={{ fontSize: 17, color: C.body, marginTop: '14px', maxWidth: '44rem' }}>{calc.description}</Box>
        </Box>

        <Box sx={{ maxWidth: 900, margin: '40px auto 0', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 0.9fr' }, gap: '24px', alignItems: 'start' }}>
          <Box sx={{ background: C.panel, borderRadius: '24px', padding: '32px' }}>
            <Box sx={{ fontFamily: F.display, fontSize: 22, marginBottom: '22px' }}>Your figures</Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {def.fields.map((f) => (
                <Box component="label" key={f.key} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Box sx={{ fontSize: 14, color: C.body }}>{f.label}</Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', background: C.white, border: '1px solid rgba(0,0,0,0.1)', borderRadius: '14px', padding: '0 16px' }}>
                    {f.prefix && <Box component="span" sx={{ color: C.muted, fontSize: 15 }}>{f.prefix}</Box>}
                    <Box
                      component="input"
                      type="number"
                      value={inputs[f.key]}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = parseFloat(e.target.value) || 0;
                        setInputs((prev) => ({ ...prev, [f.key]: val }));
                      }}
                      sx={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', padding: '13px 8px', fontSize: 16, color: C.ink, fontFamily: F.body, width: '100%' }}
                    />
                    {f.suffix && <Box component="span" sx={{ color: C.muted, fontSize: 15 }}>{f.suffix}</Box>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ background: C.ink, borderRadius: '24px', padding: '32px', color: C.white, position: { md: 'sticky' }, top: { md: 96 } }}>
            <Box sx={{ fontSize: 13, letterSpacing: '0.08rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>{out.resultLabel}</Box>
            <Box sx={{ fontFamily: F.display, fontSize: 56, lineHeight: 1, letterSpacing: '-0.03em', marginTop: '12px', color: C.white }}>{out.resultValue}</Box>
            <Box sx={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {out.breakdown.map(([k, val]) => (
                <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: 14, borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '14px' }}>
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.7)' }}>{k}</Box>
                  <Box component="span" sx={{ color: C.white, fontWeight: 500 }}>{val}</Box>
                </Box>
              ))}
            </Box>
            <Box
              component={RouterLink}
              to="/register"
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.accent, color: C.white, borderRadius: '32px', padding: '12px 20px', fontSize: 15, marginTop: '28px', '&:hover': { background: C.white, color: C.ink } }}
            >
              Track this in MillOS
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 900, margin: '32px auto 0', background: C.tintBlue, borderRadius: '20px', padding: '22px 26px', fontSize: 14, color: C.body, lineHeight: 1.6 }}>
          {out.note}
        </Box>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default CalculatorPage;
